/* Core & libraries */
import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import ContainerFluid from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import InputGroup from 'react-bootstrap/InputGroup';
import { Formik, Field, ErrorMessage, FieldArray } from "formik";
import Button from 'react-bootstrap/Button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import Tooltip from "react-bootstrap/Tooltip";
import { Link } from "react-router-dom";
import * as DOMPurify from 'dompurify';

/* Helpers, constants, custom methods, internal libraries */
import { trackEvent } from "../../../services/mixpanelUtil";
import {
  createValidationPolicyYupSchema,
  useValidationService
} from "../../../resources/validations/validations";
import { useGeneralService } from "../../../services/generalService";
import { SETTINGS_EMAILDOMAINS_ROUTE } from "../../../constants/routes";
import { toReversedHelper } from "../../../helpers/general.js";
import { useUserContext } from "../../../contexts/userContext";

/* Components */
import UCButton from "../../buttons";
import UCSelect from "../../ucselect";
import { ReactComponent as CopySvg } from '../../../resources/images/copy.svg';
import { ReactComponent as InfoIconSvg } from '../../../resources/images/info-icon.svg';
import Divider from "../../divider";
import EmailTextarea from "./emailTextarea";

/* Styles */
import styles from "./styles.js"
import generalStyles from "../../../resources/styles/general";

const Content = props => {
  const { setShowModal, sharedLink, handleSendInvites, active, requiresDomain } = props;

  const formRef = useRef(null);

  const theme = useTheme();
  const classes = styles({ theme });
  const general = generalStyles({ theme });
  const intl = useIntl();
  const { formatMessage } = useIntl();

  const { invitePeopleMockedApiValidationObj } = useValidationService()
  const [testSchema, setTestSchema] = useState({});

  const [roles, setRoles] = useState([]);

  const { getRoles } = useGeneralService();
  const userDetails = useUserContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  const messages = defineMessages({
    emailAria: {
      id: "invitepeoplemodal.emailTooltipTitle"
    },
    emailAria2: {
      id: "invitepeoplemodal.emailTooltipMessage"
    },
    roleAria: {
      id: "invitepeoplemodal.userRole"
    },
    roleAria2: {
      id: `invitepeoplemodal.info1.${providerCode}`
    },
    roleAria3: {
      id: `invitepeoplemodal.info2.${providerCode}`
    },
    roleAria4: {
      id: `invitepeoplemodal.info3.${providerCode}`
    },
    selectRoleAria: {
      id: "people.allRoles"
    },
    roleLabelAria: {
      id: `inviteRoles.7.${providerCode}`
    },
    invitePeoplemodalCancelLabel: {
      id: "invitepeoplemodal.cancel"
    },
    invitePeoplemodalSendLabel: {
      id: "invitepeoplemodal.sendinvites"
    },
  });


  useEffect(() => {
    (async () => {
      //TODO: api call to get validations //simpleMockedApiValidationObj
      const result = createValidationPolicyYupSchema(invitePeopleMockedApiValidationObj);
      setTestSchema(result)

      // const responseRoles = await getRoles();
      // if (responseRoles) {
      //   let rolesArray = [];
      //   let rolesArrReversed = toReversedHelper(responseRoles?.roles)
      //      rolesArrReversed?.map(roleId => {
      //     rolesArray.push({ value: roleId, label: <FormattedMessage id={`inviteRoles.${roleId}.${providerCode}`} /> })
      //   })
      //   setRoles(rolesArray);
      // }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      //TODO: api call to get validations //simpleMockedApiValidationObj
      const responseRoles = await getRoles();
      if (responseRoles) {
        let rolesArray = [];
        let rolesArrReversed = toReversedHelper(responseRoles?.roles)

        rolesArrReversed?.map(roleId => {
          rolesArray.push({ value: roleId, label: intl.formatMessage({id: `inviteRoles.${roleId}.${providerCode}`})})
        })
        setRoles(rolesArray);
      }
    })();
  }, []);

  const trackCopyButtonClick = () => {
    trackEvent("Copy Button Clicked", {
        "Button Name": "Copy Shared Link",
        "Shared Link": sharedLink
    });
}

  const handleFormSubmit = () => {
    trackEvent("Send Invites Button Clicked", {
      "Button Name": "Send Invites"
    });

    if (formRef.current) {
      formRef.current.handleSubmit()
    }
  }


  const submitForm = async (values, setSubmitting) => {
    try {
      console.log(values);
      const response = await handleSendInvites(values);
      console.log(values);
      console.log(response);
      trackEvent("Invitation Sent", {
        "Number of Emails Sent": values.emailAddresses.length,
        "Status": "Successful",
        "Error Type": ""  
      });
  
      setSubmitting(false);
  
    } catch (error) {
      console.error("Invitation Error:", error); 
  
      trackEvent("Invitation Sent", {
        "Number of Emails Sent": values.emailAddresses.length,
        "Status": "Error",
        "Error Type": error.message || "Unknown Error" 
      });
  
      setSubmitting(false);
    }
  }  


  const renderPopover = () => {
    return (
      <Popover className={classes.popover}>
        <div className={classes.popoverDiv} role="status" aria-live="polite">
          <FormattedMessage id="invitepeoplemodal.copied" />
        </div>
      </Popover>
    )
  }

  return (
    <ContainerFluid className={classes.content}>
      <div className={classes.roleText}>
        <label htmlFor="emailAddresses" tabIndex={0}>
          <FormattedMessage id="invitepeoplemodal.emailAddresses"/>
        </label>
        <OverlayTrigger
          placement="top"
          trigger={['hover', 'focus']}
          overlay={
            <Tooltip className={`${general.main} ${classes.tooltip}`}>
              <div className={classes.infoTitle}>
                {intl.formatMessage({id: "invitepeoplemodal.emailTooltipTitle"})}
              </div>
              <div className={classes.infoDescriptionAdmin}>
                {intl.formatMessage({id: "invitepeoplemodal.emailTooltipMessage"})}
              </div>
            </Tooltip>
          }
        >
          <InfoIconSvg className={classes.infoIcon} tabIndex="0" role="tooltip" aria-label={`${formatMessage(messages.emailAria)} ${formatMessage(messages.emailAria2)}`}/>
        </OverlayTrigger>
      </div>
      <Formik
        innerRef={formRef}
        initialValues={{
          emailAddresses: [],
          role: 7, //todo: get value from API
        }}
        validationSchema={testSchema}
        onSubmit={(values, { setSubmitting }) => {
          submitForm(values, setSubmitting);
        }}
      >
        {({
          values,
          errors,
          touched,
          setFieldTouched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue
        }) => (
          <>
            <form onSubmit={handleSubmit} >
              <FieldArray
                type="text"
                name="emailAddresses"
                id="emailAddresses"
                render={(arrayHelpers) => {
                  return (
                    <div>
                      <EmailTextarea
                        emails={values.emailAddresses}
                        setArrayHelpers={(items) => {
                          arrayHelpers.push(items);
                          setFieldTouched("emailAddresses", true)
                        }}
                        removeArrayHelpers={(id) => arrayHelpers.remove(id)}
                        updateArrayHelpers={(items) => setFieldValue("emailAddresses", items)}
                        errors={errors.emailAddresses}
                        describedByIds="email-error email-note"
                      />
                      <ErrorMessage
                        name="emailAddresses"
                        component="span"
                        className={classes.error}
                        id="email-error"
                        role="alert"
                      />
                    </div>
                  )
                }}
                className={
                  errors.emailAddresses && touched.emailAddresses
                    ? "input-error"
                    : null
                }
              />
              {/*/!*todo: create error component*!/*/}
              <div className={classes.text} id="email-note">
                <FormattedMessage id="invitepeoplemodal.inviteNumber" />
              </div>
              <div className={classes.roleText}>
                <label 
                  tabIndex={0}
                  htmlFor="role"
                >
                  <FormattedMessage id="invitepeoplemodal.role"/>
                </label>
                <OverlayTrigger
                  placement="top"
                  trigger={['hover', 'focus']}
                  overlay={
                    <Tooltip className={`${general.main} ${classes.tooltip}`}>
                      <div className={classes.infoTitle}>
                        {intl.formatMessage({id: "invitepeoplemodal.userRole"})}
                      </div>
                      {roles?.some(r => r.value === 6) && (
                        <div className={classes.infoDescriptionAdmin}>
                          {intl.formatMessage({id: `invitepeoplemodal.info3.${providerCode}`})}
                        </div>
                      )}
                      <div className={classes.infoDescriptionAdmin}>
                        {intl.formatMessage({id: `invitepeoplemodal.info1.${providerCode}`})}
                      </div>
                      <div className={classes.infoDescription}>
                        {intl.formatMessage({id: `invitepeoplemodal.info2.${providerCode}`})}
                      </div>
                    </Tooltip>
                  }  
                >
                  <InfoIconSvg className={classes.infoIcon} tabIndex="0" role="tooltip" aria-label={`${formatMessage(messages.roleAria)} ${formatMessage(messages.roleAria2)} ${formatMessage(messages.roleAria3)} ${formatMessage(messages.roleAria4)}`}/>
                </OverlayTrigger>
              </div>
              {
                roles &&
                <Field
                  type="select"
                  name="role"
                  id="role"
                  render={({ form }) => (
                    <div className={classes.rolesWrapper}>
                      <UCSelect
                        className={classes.roles}
                        displayName={<FormattedMessage id="people.allRoles" />}
                        options={roles}
                        defaultValue={{ value: 7, label: formatMessage(messages.roleLabelAria) }}
                        onChange={(item) => form.setFieldValue("role", item.value)}
                        getOptionLabel={(option) => option.label}
                        ariaLabel={formatMessage(messages.selectRoleAria)}
                      />
                      <ErrorMessage
                        name="role"
                        component="span"
                        className={classes.error}
                      />
                    </div>
                  )}
                  tabIndex={0}
                />
              }
              <Row className={classes.btnRow} aria-live="assertive">
                <Col className={classes.cancelButtonRow}>
                  <UCButton
                    onClick={() => setShowModal(false)}
                    type="default"
                    className={classes.cancelBtn}
                    text={<FormattedMessage id="invitepeoplemodal.cancel" />}
                    ariaLabel={formatMessage(messages.invitePeoplemodalCancelLabel)}
                    tabIndex={0}
                  />
                </Col>
                <Col className={classes.sendButtonRow}>
                  <UCButton
                    className={classes.saveBtn}
                    onClick={handleFormSubmit}
                    type="primary"
                    text={<FormattedMessage id="invitepeoplemodal.sendinvites" />}
                    tabIndex={0}
                    ariaLabel={formatMessage(messages.invitePeoplemodalSendLabel)}
                    spinner={{
                      name: "sendInviteSpinner",
                      size: 20,
                    }}
                  />
                </Col>
              </Row>
            </form>
          </>
        )}
      </Formik>

      <Row className={classes.dividerStyle} aria-hidden="true">
        <Divider tabIndex="-1"><FormattedMessage id="invitepeoplemodal.or" /></Divider>
      </Row>
      <div className={classes.roleText}>
        <FormattedMessage id="invitepeoplemodal.share" />
      </div>
      <Row>
        <InputGroup className={classes.inputGroupWrapper}>
          <InputGroup.Text className={`${classes.inputGroup} ${requiresDomain && !active ? classes.disabled : ""}`}>
            {sharedLink}
          </InputGroup.Text>
          <OverlayTrigger trigger="click" rootClose placement="top" overlay={renderPopover()} onEntered={(node) => node.focus()}>
            <InputGroup.Text className={`${classes.inputGroup} ${requiresDomain && !active ? classes.disabled : ""}`}>
              <Button className={`${classes.inputGroupButton} ${requiresDomain && !active ? classes.disabledButton : ""}`} variant="light" 
              onClick={() => { 
                navigator.clipboard.writeText(sharedLink) 
                trackCopyButtonClick()
              }}
              aria-label={intl.formatMessage({id: "invitepeoplemodal.copyAriaLabel"}) + sharedLink}
              >
                <span className={classes.inputCopyWrapper}>
                  <div className={classes.inputCopyIcon}>
                    <CopySvg />
                  </div>
                  <div className={`${classes.inputCopyText} ${requiresDomain && !active ? classes.disabledButton : ""}`}>
                    <FormattedMessage id="invitepeoplemodal.copy"/>
                  </div>
                </span>
              </Button>
            </InputGroup.Text>
          </OverlayTrigger>
        </InputGroup>
      </Row>
      {
        requiresDomain && !active ?
          <div className={classes.manageText}>
            <FormattedMessage id="invitepeoplemodal.addCompany" />
            <Link onClick={() => setShowModal(false)} to={SETTINGS_EMAILDOMAINS_ROUTE} aria-label={intl.formatMessage({id: "invitepeoplemodal.addCompany"}) + intl.formatMessage({id: "invitepeoplemodal.linkText"})}>
              <FormattedMessage id="invitepeoplemodal.linkText" />
            </Link>.
          </div>
          :
          <div className={classes.manageText}>
            <FormattedMessage id="invitepeoplemodal.manage" />
            <Link onClick={() => setShowModal(false)} to={SETTINGS_EMAILDOMAINS_ROUTE} aria-label={intl.formatMessage({id: "invitepeoplemodal.manage"}) + intl.formatMessage({id: "invitepeoplemodal.linkText"})}>
              <FormattedMessage id="invitepeoplemodal.linkText" />
            </Link>.
          </div>
      }
    </ContainerFluid>
  )
}

export default Content;
